import type { ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import { Spinner as SpinnerPrimitive } from './shadcn/spinner';
import { Button } from './Button';
import { Empty, EmptyContent, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from './shadcn/empty';
import { Skeleton } from './shadcn/skeleton';
import { useTranslation } from '../../lib/i18n';
import { cn } from '../../lib/utils';

/** "There is nothing here", said once, composed from the shadcn/ui `Empty` parts in `./shadcn/empty.tsx`.
 *
 *  The parts are still named — `empty-state__*` — for the same reason the field and the toolbar are:
 *  this block appears inside every register in the app, so a design has to be able to restate it without
 *  every module passing a prop about how it should look. The studio skin frames `.empty-state` as a
 *  direct child of a register and rewrites the title and the description, so those three class names and
 *  the position of the element that carries `.empty-state` are part of the contract, not decoration. */
export function EmptyState({ title, description, icon: Icon, action }: { title: string; description?: string; icon?: LucideIcon; action?: ReactNode }) {
  return (
    <Empty className="empty-state animate-fade-up">
      {Icon ? <EmptyMedia><Icon size={28} strokeWidth={1.25} aria-hidden /></EmptyMedia> : null}
      <EmptyHeader>
        <EmptyTitle className="empty-state__title text-eyebrow uppercase">{title}</EmptyTitle>
        {description && <EmptyDescription className="empty-state__description">{description}</EmptyDescription>}
      </EmptyHeader>
      {action ? <EmptyContent>{action}</EmptyContent> : null}
    </Empty>
  );
}

const SPINNER_PX = { xs: 10, sm: 13, md: 16, toast: 22, lg: 40 } as const;

/** The turning-circle indicator in named sizes for tool pills, inline progress, toasts and overlays.
 *  No free pixel prop: each size fits an existing surface. The toast's reduced-motion class is supplied
 *  by that caller; other indicators retain the host's effects policy.
 *  Without `label` it is `aria-hidden`: a spinner next to visible text would be announced twice. */
export function Spinner({ size = 'sm', tone = 'text-muted-foreground', label, className }: { size?: keyof typeof SPINNER_PX; tone?: string; label?: string; className?: string }) {
  return (
    <SpinnerPrimitive
      width={SPINNER_PX[size]}
      height={SPINNER_PX[size]}
      className={cn('size-auto', tone, className)}
      {...(label ? { role: 'status' as const, 'aria-label': label } : { 'aria-hidden': true })}
    />
  );
}

/** Loading as a LINE OF TEXT, for the places a skeleton would be wrong: inside a dropdown, a monospace
 *  log tail, a single field. `inline` deliberately inherits the parent's typography — those sit among
 *  sibling rows and should match them rather than the rest of the app. */
export function LoadingLine({ label, layout = 'block' }: { label?: string; layout?: 'inline' | 'block' | 'page' }) {
  const { t } = useTranslation();
  const box = layout === 'page'
    ? 'flex h-screen items-center justify-center gap-2'
    : layout === 'block' ? 'flex items-center justify-center gap-2 py-8' : 'inline-flex items-center gap-1.5';
  return (
    <span className={box} role="status" aria-live="polite">
      <span className={`animate-pulse text-muted-foreground ${layout === 'inline' ? '' : 'text-meta'}`}>{label ?? t.common.loading}</span>
    </span>
  );
}

type SkeletonVariant = 'list' | 'cards' | 'block';

/** Skeleton placeholder shaped like the real content so the layout doesn't pop in. Every block is the
 *  shadcn/ui `Skeleton` from `./shadcn/skeleton.tsx`, which is what keeps the reduced-effects setting
 *  reaching all four variants — see the note in that file. */
export function LoadingState({ variant = 'list', height = 'h-28' }: { variant?: SkeletonVariant; height?: string }) {
  const { t } = useTranslation();
  // One plain block, for a chart or panel whose shape says nothing useful while it is empty.
  if (variant === 'block') return <Skeleton className={`w-full ${height}`} aria-busy="true" aria-label={t.common.loading} />;

  if (variant === 'cards') {
    return (
      <div className="@container" aria-busy="true" aria-label={t.common.loading}>
      <div className="grid grid-cols-1 gap-3 @sm:grid-cols-2 @3xl:grid-cols-3">
        {[0, 1, 2, 3, 4, 5].map((i) => (
          <div key={i} className="flex flex-col gap-2.5 rounded-lg border border-border bg-card p-3 shadow-card">
            <div className="flex items-center gap-2.5">
              <Skeleton className="h-9 w-9 rounded-lg" />
              <div className="flex flex-1 flex-col gap-1.5">
                <Skeleton className="h-3.5 w-2/3 rounded" />
                <Skeleton className="h-3 w-1/3 rounded" />
              </div>
            </div>
            <Skeleton className="h-6 w-full" />
          </div>
        ))}
      </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2.5 py-2" aria-busy="true" aria-label={t.common.loading}>
      {[88, 72, 80, 64].map((w, i) => (
        <div key={i} className="flex items-center gap-3">
          <Skeleton className="h-4 w-4" />
          <Skeleton className="h-3.5 rounded" style={{ width: `${w}%` }} />
        </div>
      ))}
    </div>
  );
}

/** A load that failed, in the place its result would have gone. It shares `Empty` with `EmptyState`
 *  rather than shadcn's `Alert`: this is a whole panel standing in for its content, not a banner beside
 *  it, and several call sites already wrap it in a danger-toned `ControlSurfaceState` whose frame an
 *  Alert's own box would sit inside. */
export function ErrorState({ message, onRetry }: { message: string; onRetry?: () => void }) {
  const { t } = useTranslation();
  return (
    // `role="alert"` because this block REPLACES the content the user asked for: it appears after the
    // request has failed, and without a live region a screen-reader user is left on a region that simply
    // never fills in. It is on the wrapper rather than on the title so the retry control is announced with
    // the message that explains why it is there.
    <Empty className="gap-2 py-12" role="alert">
      {/* The message is the failure, so it takes the destructive colour. `text-primary` painted it the
          brand ember, which on the built-in design is the same hue as danger and reads as an error only
          by coincidence — under studio-light the ember is a signal BLUE and a failure was announced in
          the colour that design uses for links. */}
      <EmptyTitle className="text-destructive">{message}</EmptyTitle>
      {onRetry && <EmptyContent><Button onClick={onRetry}>{t.common.retry}</Button></EmptyContent>}
    </Empty>
  );
}

/** A standing condition about the surface the user is looking at — capture switched off, a legacy
 *  payload, a request that failed — drawn as a strip where that content begins. It is NOT a toast: a
 *  toast reports what just happened and leaves, this states what is still true, so it stays for as long
 *  as the condition does.
 *
 *  Three call sites hand-painted the same tinted border-and-wash box, which the caller's own eye reads as
 *  one pattern and the guard reads as a copied skin. The tones are the state vocabulary
 *  (`--color-warning`, `--color-destructive`) at the badge-carrying 40% edge over a 10% wash of the same
 *  state — the pairing `Badge variant="soft-*"` already uses, so a skin moves both together.
 *
 *  `action` is a trailing control of the strip's own (a ghost `Button`), pushed to the far edge. */
export function StateBanner({ children, tone = 'warning', action }: { children: ReactNode; tone?: 'warning' | 'danger' | 'success'; action?: ReactNode }) {
  // Stated as whole class names, never composed: Tailwind's scanner reads source text, so `bg-${tone}/10`
  // would emit no utility at all and the strip would paint its ink on nothing.
  const skin = {
    warning: 'border-warning/40 bg-warning/10 text-warning-ink',
    danger: 'border-destructive/40 bg-destructive/10 text-destructive-ink',
    success: 'border-success/40 bg-success/10 text-success-ink',
  }[tone];
  return (
    <div data-slot="state-banner" data-tone={tone} role={tone === 'danger' ? 'alert' : 'status'}
      className={`flex min-w-0 flex-wrap items-center gap-2 border-b px-4 py-2 text-meta ${skin}`}>
      {children}
      {action ? <div className="ml-auto flex shrink-0 items-center gap-2">{action}</div> : null}
    </div>
  );
}
